import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ROUTES } from '../../consts/general';
import { AMAZON_FBA_TRANSFER_HISTORY } from '../../consts/reports';
import { formatQty } from '../fbaTransfers/format';

const BUCKETS = [
  ['EXACT', 'Exact match'],
  ['PARTIAL', 'Partial match'],
  ['NO_MATCH', 'No ShipHero removal'],
  ['ORPHAN', 'Removal with no shipment'],
  ['NEEDS_REVIEW', 'Needs review'],
  ['CANCELLED_NO_MOVEMENT', 'Cancelled — no movement'],
  ['INVENTORY_RECOUNT', 'Non-Amazon — inventory recount'],
  ['STORE_RECLASSIFICATION', 'Non-Amazon — store reclassification'],
  ['RECEIVING_CLEANUP', 'Non-Amazon — receiving cleanup'],
];
const BUCKET_LABELS = Object.fromEntries(BUCKETS);
const QTY = new Set(['qty_shipped', 'qty_received', 'matched_qty']);
const ordinal = (day) => {
  if (day % 100 >= 11 && day % 100 <= 13) return `${day}th`;
  return `${day}${{ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th'}`;
};
const longDate = (ymd) => {
  if (!ymd) return '—';
  const date = new Date(`${ymd}T12:00:00Z`);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
  const month = date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
  return `${weekday}, ${month} ${ordinal(date.getUTCDate())}, ${date.getUTCFullYear()}`;
};

const cell = (row, key) => {
  if (key === 'bucket') return BUCKET_LABELS[row.bucket] || row.bucket;
  if (key === 'shipment_date') return longDate(row.shipment_date);
  if (QTY.has(key)) return row[key] == null ? '—' : formatQty(row[key]);
  return row[key] || '—';
};

const exportHistory = async (bucket) => {
  const params = new URLSearchParams();
  if (bucket) params.set('bucket', bucket);
  const response = await fetch(`/api/v1/reports/${AMAZON_FBA_TRANSFER_HISTORY}/export?${params.toString()}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (!response.ok) throw new Error('Failed to export the Amazon FBA transfer history');
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = 'amazon-fba-transfer-history.csv';
  link.click();
  URL.revokeObjectURL(url);
  return { handled: true };
};

const AmazonFbaTransferHistoryReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState('');
  const [state, setState] = useState({ rows: [], columns: [], totals: null, loading: true, error: null });

  useEffect(() => {
    const controller = new AbortController();
    setState((current) => ({ ...current, loading: true, error: null }));
    axios.get(`/api/v1/reports/${AMAZON_FBA_TRANSFER_HISTORY}`, { params: { bucket: bucket || undefined }, signal: controller.signal })
      .then(({ data }) => setState({ rows: data.data || [], columns: data.columns || [], totals: data.totals || null, loading: false, error: null }))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        setState((current) => ({ ...current, loading: false, error: error?.response?.data?.error || 'Failed to load the Amazon FBA transfer history' }));
      });
    return () => controller.abort();
  }, [bucket]);

  const { rows, columns, totals, loading, error } = state;
  return (
    <ReportTemplate
      reportId={AMAZON_FBA_TRANSFER_HISTORY}
      title="Amazon FBA Transfer History"
      description="2026 Amazon FBA shipments reconstructed against the team's manual ShipHero removals. Imported history only; nothing here moved inventory."
      breadcrumbs={[{ label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> }, { label: '' }]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      filters={(
        <select value={bucket} onChange={(event) => setBucket(event.target.value)} className="rounded-md border border-gray-300 px-2 py-2 text-sm">
          <option value="">All results</option>
          {BUCKETS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      )}
      exportButton={<CSVExportButton data={rows} columns={[]} filename="amazon-fba-transfer-history" onExport={() => exportHistory(bucket)} />}
      isLoading={loading}
      renderChildrenOnly
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-4 text-sm text-gray-700">
          <span><strong>{formatQty(totals?.lines ?? 0)}</strong> lines</span>
          {BUCKETS.map(([value, label]) => <span key={value}><strong>{formatQty(totals?.[value] ?? 0)}</strong> {label.toLowerCase()}</span>)}
        </div>
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="border-b border-gray-200">
                {columns.map((col) => <th key={col.key} className={`whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500 ${QTY.has(col.key) ? 'text-right' : 'text-left'}`}>{col.label}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={columns.length || 1}>Loading…</td></tr>}
              {!loading && !rows.length && <tr><td className="px-4 py-6 text-sm text-gray-500" colSpan={columns.length || 1}>No reconstruction has been saved yet.</td></tr>}
              {!loading && rows.map((row) => (
                <tr key={row.id}>
                  {columns.map((col) => <td key={col.key} className={`whitespace-nowrap px-4 py-3 text-sm text-gray-900 ${QTY.has(col.key) ? 'text-right tabular-nums' : ''}`}>{cell(row, col.key)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ReportTemplate>
  );
};

export default AmazonFbaTransferHistoryReport;
